import React, { useState, useMemo } from 'react';
import { Box, Typography, useTheme, Button, CircularProgress } from '@mui/material';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useSelector } from 'react-redux';
import { UserRole } from '@/types/user';
import { RootState } from '@/store/store';
import {
  useExportTransactionSummaryMutation,
  useGetTransactionSummaryQuery,
} from '@/services/dashboardApi';
import excelIcon from '../../assets/excel-icon.svg';

const PERIODS = ['1W', '1M', '6M', '1Y', 'All'] as const;
type Period = (typeof PERIODS)[number];

function getDateRange(period: Period): { startDate?: string; endDate?: string } {
  if (period === 'All') return {};
  const end = new Date();
  const start = new Date();
  switch (period) {
    case '1W': start.setDate(end.getDate() - 7); break;
    case '1M': start.setMonth(end.getMonth() - 1); break;
    case '6M': start.setMonth(end.getMonth() - 6); break;
    case '1Y': start.setFullYear(end.getFullYear() - 1); break;
  }
  const fmt = (d: Date) => d.toISOString().split('T')[0];
  return { startDate: fmt(start), endDate: fmt(end) };
}

interface Props {
  amcId?: string;
}

export const TransactionSummaryChart: React.FC<Props> = ({ amcId }) => {
  const theme = useTheme();
  const role = useSelector((state: RootState) => state.user.role) as UserRole | undefined;
  const [exportTransactionSummary, { isLoading: isDownloading }] =
    useExportTransactionSummaryMutation();

  const [period, setPeriod] = useState<Period>('1W');
  const dateRange = useMemo(() => getDateRange(period), [period]);

  const { data, isFetching } = useGetTransactionSummaryQuery(
    { amcId, ...dateRange },
  );

  // Role-based access control
  const isAuthorized =
    role === UserRole.AMC_ADMIN || role === UserRole.ADMIN || role === UserRole.SUPPORT;
  if (!isAuthorized) return null;

  // AMC context for display
  const isAmcSpecific = Boolean(amcId);
  const chartTitle = isAmcSpecific
    ? `Transaction Processing Analysis - AMC ${amcId}`
    : 'Transaction Processing Analysis';

  // Download function using RTK Query
  const handleDownload = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    try {
      const blob = await exportTransactionSummary({ amcId, ...dateRange }).unwrap();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.download = 'transaction-summary.xlsx'; // Let backend handle filename via Content-Disposition

      document.body.appendChild(link);

      // Use requestAnimationFrame to ensure proper download
      requestAnimationFrame(() => {
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      });
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  // Handle empty data case
  if (!data || !data.stats || data.stats.totalAnalyzed === 0) {
    return (
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '10px',
          p: 3,
          flex: 1,
          minWidth: '300px',
          textAlign: 'center',
        }}
      >
        {/* Period filter - centered */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mb: 2 }}>
          {PERIODS.map((p) => (
            <Button
              key={p}
              variant="outlined"
              size="small"
              onClick={() => setPeriod(p)}
              sx={{
                px: 2,
                py: 0.5,
                fontSize: '13px',
                fontWeight: period === p ? 600 : 400,
                textTransform: 'none',
                borderRadius: '20px',
                borderColor: period === p ? theme.palette.primary.main : theme.palette.divider,
                color: period === p ? theme.palette.primary.main : theme.palette.text.secondary,
                backgroundColor: 'transparent',
                minWidth: 'auto',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: theme.palette.action.hover,
                },
              }}
              aria-pressed={period === p}
            >
              {p}
            </Button>
          ))}
          {isFetching && <CircularProgress size={16} />}
        </Box>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          {chartTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No data available for the selected period
        </Typography>
      </Box>
    );
  }

  const { stats } = data;

  // Chart configuration
  const chartOptions: Highcharts.Options = {
    credits: { enabled: false },
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
      height: '380px',
      spacingTop: 20,
      spacingBottom: 20,
      spacingLeft: 20,
      spacingRight: 20,
    },
    title: {
      text: chartTitle,
      style: {
        fontSize: '16px',
        fontWeight: '600',
        color: theme.palette.text.primary,
        fontFamily: theme.typography.fontFamily,
      } as Highcharts.CSSObject,
    },
    subtitle: {
      text: `Total: ${stats.totalAnalyzed} | Query API: ${stats.totalQueryApi} | Non Query API: ${stats.totalNonQueryApi}`,
      style: {
        fontSize: '12px',
        color: theme.palette.text.secondary,
        fontWeight: 'normal',
      } as Highcharts.CSSObject,
    },
    xAxis: {
      categories: ['Query API', 'Non Query API'],
      title: { text: '' },
      labels: {
        style: {
          color: theme.palette.text.primary,
          fontSize: '12px',
          fontWeight: 'normal',
        },
      },
      lineWidth: 0,
      tickWidth: 0,
      gridLineWidth: 0,
    },
    yAxis: {
      title: { text: '' },
      labels: {
        style: {
          color: theme.palette.text.secondary,
          fontSize: '11px',
        },
      },
      min: 0,
      gridLineColor: '#f0f0f0',
      gridLineWidth: 1,
      lineWidth: 0,
      tickWidth: 0,
    },
    legend: {
      itemStyle: {
        color: theme.palette.text.primary,
        fontSize: '11px',
        fontWeight: 'normal',
      },
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'top',
      y: 15,
    },
    plotOptions: {
      column: {
        dataLabels: {
          enabled: true,
          style: {
            color: theme.palette.text.primary,
            fontWeight: '600',
            fontSize: '12px',
            textOutline: 'none',
          },
          format: '{y}',
          y: -5,
        },
        borderRadius: 2,
        pointPadding: 0.2,
        groupPadding: 0.15,
        borderWidth: 0,
        shadow: false,
      },
    },
    tooltip: {
      shared: true,
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.divider,
      borderRadius: 8,
      borderWidth: 1,
      shadow: true,
      style: {
        color: theme.palette.text.primary,
        fontSize: '13px',
        fontFamily: theme.typography.fontFamily,
      },
      formatter: function () {
        const category = this.x;
        let tooltipText = `<div style="text-align: center; margin-bottom: 8px;"><b style="font-size: 14px;">${category}</b></div>`;
        this.points?.forEach((point) => {
          const total = category === 'Query API' ? stats.totalQueryApi : stats.totalNonQueryApi;
          const percentage = total > 0 ? (((point.y as number) / total) * 100).toFixed(1) : 0;
          tooltipText += `
            <div style="margin: 4px 0; display: flex; align-items: center;">
              <span style="color:${point.color}; font-size: 16px; margin-right: 8px;">●</span>
              <span>${point.series.name}: <b>${point.y}</b> (${percentage}%)</span>
            </div>
          `;
        });
        return tooltipText;
      },
    },
    series: [
      {
        type: 'column',
        name: 'Successful',
        data: [stats.queryApiSuccessful, stats.nonQueryApiSuccessful],
        color: '#10B981',
        states: {
          hover: {
            color: '#059669',
          },
        },
      },
      {
        type: 'column',
        name: 'Failed',
        data: [stats.queryApiFailed, stats.nonQueryApiFailed],
        color: '#EF4444',
        states: {
          hover: {
            color: '#DC2626',
          },
        },
      },
    ],
  };

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: '10px',
        p: 3,
        flex: 1,
        minWidth: '300px',
        position: 'relative',
      }}
    >
      {/* Period filter - centered */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mb: 1 }}>
        {PERIODS.map((p) => (
          <Button
            key={p}
            variant="outlined"
            size="small"
            onClick={() => setPeriod(p)}
            sx={{
              px: 2,
              py: 0.5,
              fontSize: '13px',
              fontWeight: period === p ? 600 : 400,
              textTransform: 'none',
              borderRadius: '20px',
              borderColor: period === p ? theme.palette.primary.main : theme.palette.divider,
              color: period === p ? theme.palette.primary.main : theme.palette.text.secondary,
              backgroundColor: 'transparent',
              minWidth: 'auto',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.action.hover,
              },
            }}
            aria-pressed={period === p}
          >
            {p}
          </Button>
        ))}
        {isFetching && <CircularProgress size={16} />}
      </Box>

      {/* Download button */}
      <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={
            isDownloading ? (
              <CircularProgress size={16} />
            ) : (
              <img src={excelIcon} alt="Export to Excel" style={{ width: 16, height: 16 }} />
            )
          }
          onClick={handleDownload}
          disabled={isDownloading}
          sx={{
            minWidth: 'auto',
            textTransform: 'none',
            fontSize: '12px',
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.light,
              borderColor: theme.palette.primary.main,
            },
          }}
          aria-label="Export to Excel"
        >
          {isDownloading ? 'Downloading...' : null}
        </Button>
      </Box>

      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </Box>
  );
};
